import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  deleteCustomPrompt,
  editCustomPrompt,
  fetchContactDetails,
  fetchContactDetailsPartial,
  fetchContactDetailsPolling,
  fetchMessages,
  fetchMessagesPolling,
  getContactDetails,
  getCustomPromptData,
  regenerateHpm,
  saveCustomPrompt,
  updateIsFavoriteCustomPrompt,
} from './hpm.actions';
import { HPMCustomLibraryProperties, HPMCustomPromptProperties } from './types';

export type InitialState = {
  customPromptData: HPMCustomPromptProperties;
  saveCustomPromptLoader: boolean;
  allContactsData: any[];
  selectedContacts: number[];
  contactsData: [];
  messagesData: Record<number, any>;
  fetchContactDetailsLoader: boolean;
  fetchMessagesLoader: Record<number, boolean>;
  profileDataFromLinkedInObj: Record<number, any>;
  sortingOrder: 'ASC' | 'DSC' | undefined;
  pendingOrderIds: string[];
  pendingContactPks: string[];
  customPromptDataForAIMessageScreen?: {
    customPrompt?: string;
    userCustomPromptId?: number;
    selectedLLMModel?: string;
    selectedColumns?: number[];
    selectedSignals?: number[];
    selectedProductAndServices?: number[];
    isEmail?: boolean;
    isConnectRequest?: boolean;
    isPostConnection?: boolean;
    isCallScript?: boolean;
    isLinkedinPostFollowUp?: boolean;
    isLinkedinPostSecondFollowUp?: boolean;
    alumniFromCollege?: boolean;
    changedJobIn?: boolean;
    gotPromoted?: boolean;
    pastColleague?: boolean;
    icpVariables?: boolean;
    techSignals?: boolean;
    isFollowUp?: boolean;
    isSecondFollowUp?: boolean;
    selectedHpmFileContents?: number[];
  };
  lastGenerationTypeForAIMessageScreen?: 'customPrompt' | 'generationCard' | undefined;
};

const initialState: InitialState = {
  customPromptData: {
    customPrompts: [],
    llmModels: [],
    ppfo: {
      accountColumns: [],
      contactColumns: [],
      insights: [],
      features: [],
      outcomes: [],
      painpoints: [],
      testimonials: [],
      productAndServices: [],
      hpmFileContents: [],
      activeCustomers: [],
    },
  },
  saveCustomPromptLoader: false,
  allContactsData: [],
  selectedContacts: [],
  contactsData: [],
  messagesData: {},
  fetchContactDetailsLoader: false,
  fetchMessagesLoader: {},
  profileDataFromLinkedInObj: {},
  sortingOrder: undefined,
  pendingOrderIds: [],
  pendingContactPks: [],
  customPromptDataForAIMessageScreen: undefined,
  lastGenerationTypeForAIMessageScreen: undefined,
};

const hpmSlice = createSlice({
  name: 'hpmV2',
  initialState,
  reducers: {
    setAllContactsData: (state, { payload }: PayloadAction<any[]>) => {
      state.allContactsData = payload;
    },
    setSortingOrder: (state, { payload }: PayloadAction<'ASC' | 'DSC' | undefined>) => {
      state.sortingOrder = payload;
    },
    clearMessagesData: (state) => {
      state.messagesData = {};
    },
    clearContactsData: (state) => {
      state.contactsData = [];
    },
    setCustomPromptDataForAIMessageScreen: (
      state,
      {
        payload,
      }: PayloadAction<{
        userCustomPromptId?: number;
        selectedLLMModel?: string;
        selectedColumns?: number[];
        selectedSignals?: number[];
        isEmail?: boolean;
        isConnectRequest?: boolean;
        isPostConnection?: boolean;
        isCallScript?: boolean;
        isLinkedinPostFollowUp?: boolean;
        isLinkedinPostSecondFollowUp?: boolean;
        isFollowUp?: boolean;
        isSecondFollowUp?: boolean;
        customPrompt?: string;
        alumniFromCollege?: boolean;
        changedJobIn?: boolean;
        gotPromoted?: boolean;
        pastColleague?: boolean;
        icpVariables?: boolean;
        techSignals?: boolean;
        selectedProductAndServices?: number[];
        selectedTestimonials?: number[];
        selectedHpmFileContents?: number[];
        selectedActiveCustomers?: number[];
      }>,
    ) => {
      state.customPromptDataForAIMessageScreen = payload;
    },
    resetCustomPromptDataForAIMessageScreen: (state) => {
      state.customPromptDataForAIMessageScreen = undefined;
    },
    setLastGenerationTypeForAIMessageScreen: (
      state,
      { payload }: PayloadAction<'customPrompt' | 'generationCard' | undefined>,
    ) => {
      state.lastGenerationTypeForAIMessageScreen = payload;
    },
    setSelectedContacts: (state, { payload }: PayloadAction<number[]>) => {
      state.selectedContacts = payload;
    },
    resetHPMMessageState: (state) => {
      state.messagesData = {};
    },
    resetMessageLoader: (state) => {
      state.fetchMessagesLoader = {};
    },
    clearPendingOrders: (state) => {
      state.pendingOrderIds = [];
      state.pendingContactPks = [];
    },
    addPendingOrder: (state, { payload }: PayloadAction<string>) => {
      if (!state.pendingOrderIds.includes(payload)) {
        state.pendingOrderIds.push(payload);
      }
    },
    removePendingOrder: (state, { payload }: PayloadAction<string>) => {
      state.pendingOrderIds = state.pendingOrderIds.filter((id) => id !== payload);
    },
    addPendingContact: (state, { payload }: PayloadAction<string>) => {
      if (!state.pendingContactPks.includes(payload)) {
        state.pendingContactPks.push(payload);
      }
    },
    removePendingContact: (state, { payload }: PayloadAction<string>) => {
      state.pendingContactPks = state.pendingContactPks.filter((pk) => pk !== payload);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      getCustomPromptData.fulfilled,
      (state, { payload }: PayloadAction<HPMCustomPromptProperties>) => {
        state.customPromptData = payload;
      },
    );
    builder.addCase(getCustomPromptData.rejected, (state) => {
      state.customPromptData = {
        customPrompts: [],
        llmModels: [],
        ppfo: {
          accountColumns: [],
          contactColumns: [],
          insights: [],
          features: [],
          outcomes: [],
          painpoints: [],
          testimonials: [],
          productAndServices: [],
          hpmFileContents: [],
          activeCustomers: [],
        },
      };
    });
    builder.addCase(saveCustomPrompt.pending, (state) => {
      state.saveCustomPromptLoader = true;
    });
    builder.addCase(
      saveCustomPrompt.fulfilled,
      (state, { payload }: PayloadAction<HPMCustomLibraryProperties>) => {
        state.saveCustomPromptLoader = false;
        state.customPromptData = {
          ...state.customPromptData,
          customPrompts: [...state.customPromptData.customPrompts, payload],
        };
      },
    );
    builder.addCase(saveCustomPrompt.rejected, (state) => {
      state.saveCustomPromptLoader = false;
    });
    builder.addCase(
      updateIsFavoriteCustomPrompt.fulfilled,
      (state, { payload }: PayloadAction<number>) => {
        state.customPromptData = {
          ...state.customPromptData,
          customPrompts: state.customPromptData.customPrompts.map((prompt) =>
            prompt.primaryKey === payload ? { ...prompt, isFavorite: !prompt.isFavorite } : prompt,
          ),
        };
      },
    );
    builder.addCase(deleteCustomPrompt.fulfilled, (state, { payload }: PayloadAction<number>) => {
      state.customPromptData = {
        ...state.customPromptData,
        customPrompts: state.customPromptData.customPrompts.filter(
          (prompt) => prompt.primaryKey !== payload,
        ),
      };
    });
    builder.addCase(
      editCustomPrompt.fulfilled,
      (state, { payload }: PayloadAction<HPMCustomLibraryProperties>) => {
        state.customPromptData = {
          ...state.customPromptData,
          customPrompts: state.customPromptData.customPrompts.map((item) => {
            if (item.primaryKey === payload.primaryKey) {
              return payload;
            }
            return item;
          }),
        };
      },
    );
    builder.addCase(fetchContactDetails.pending, (state) => {
      state.fetchContactDetailsLoader = true;
      state.contactsData = [];
    });
    builder.addCase(fetchContactDetails.fulfilled, (state, { payload }: PayloadAction<any>) => {
      state.fetchContactDetailsLoader = false;
      state.contactsData = payload;
    });
    builder.addCase(fetchContactDetails.rejected, (state) => {
      state.fetchContactDetailsLoader = false;
    });
    builder.addCase(
      fetchContactDetailsPolling.fulfilled,
      (state, { payload }: PayloadAction<any>) => {
        state.contactsData = payload;
      },
    );
    builder.addCase(
      fetchContactDetailsPartial.fulfilled,
      (state, { payload }: PayloadAction<any[]>) => {
        const updatedMap = new Map(
          (state.contactsData as any[]).map((c: any) => [c.contactPk, c]),
        );
        payload.forEach((contact: any) => updatedMap.set(contact.contactPk, contact));
        state.contactsData = Array.from(updatedMap.values()) as [];
      },
    );
    builder.addCase(
      getContactDetails.fulfilled,
      (
        state,
        {
          payload,
          meta: {
            arg: { contactPk },
          },
        },
      ) => {
        if (!state.profileDataFromLinkedInObj) {
          state.profileDataFromLinkedInObj = {};
        }
        state.profileDataFromLinkedInObj[contactPk] = payload as any;
      },
    );
    builder.addCase(fetchMessages.pending, (state, { meta }) => {
      state.fetchMessagesLoader[meta.arg.contactPk] = true;
    });
    builder.addCase(fetchMessages.fulfilled, (state, { payload }: PayloadAction<any>) => {
      const dataPk = Object.keys(payload)[0] || 0;
      state.fetchMessagesLoader[dataPk as number] = false;
      state.messagesData = {
        ...state.messagesData,
        [dataPk]: payload[dataPk],
      };
    });
    builder.addCase(fetchMessagesPolling.fulfilled, (state, { payload }: PayloadAction<any>) => {
      const dataPk = Object.keys(payload)[0] || 0;
      state.messagesData = {
        ...state.messagesData,
        [dataPk]: payload[dataPk],
      };
    });
    builder.addCase(regenerateHpm.fulfilled, (state) => {
      state.messagesData = {};
    });
  },
});

export const {
  setAllContactsData,
  setSortingOrder,
  clearMessagesData,
  clearContactsData,
  setCustomPromptDataForAIMessageScreen,
  resetCustomPromptDataForAIMessageScreen,
  setLastGenerationTypeForAIMessageScreen,
  setSelectedContacts,
  resetHPMMessageState,
  resetMessageLoader,
  clearPendingOrders,
  addPendingOrder,
  removePendingOrder,
  addPendingContact,
  removePendingContact,
} = hpmSlice.actions;

export default hpmSlice.reducer;
